import axios from 'axios';
import envConfig from '../config/env';

const adminApi = axios.create({
  baseURL: `${envConfig.apiUrl}/api/admin`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const userApi = axios.create({
  baseURL: `${envConfig.apiUrl}/api/user`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    delete config.headers.Authorization;
  }
  config.headers.Origin = window.location.origin;
  return config;
});

userApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('userToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    delete config.headers.Authorization;
  }
  return config;
});

adminApi.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

userApi.interceptors.response.use(
  (response) => response.data,
  (error) => Promise.reject(error)
);

export const login = async ({ username, password }) => {
  const response = await adminApi.post('/login', { username, password });
  if (!response.success) {
    throw new Error(response.message || 'Login failed');
  }

  localStorage.setItem('adminToken', response.data?.token || '');
  localStorage.setItem('adminUserId', response.data?.id || '');
  localStorage.setItem('user', JSON.stringify(response.data || {}));
  return response.data;
};

export const logout = async () => {
  const response = await adminApi.post('/logout');
  if (!response.success) {
    throw new Error(response.message || 'Logout failed');
  }

  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminUserId');
  return response.data;
};

export const getAdminUsers = (params = {}) => {
  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null)
  );

  return adminApi.get('/getUsers', {
    params: filteredParams,
  });
};

export const createCoupon = async (data) => {
  const response = await adminApi.post('/creatcoupons', data);
  if (!response.success) {
    throw new Error(response.message || 'Create coupon failed');
  }
  return response;
};

export const getCoupons = (params = {}) => {
  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null)
  );

  return adminApi.get('/getCoupons', {
    params: filteredParams,
  });
};

export const markCouponUsed = (code) => adminApi.post('/useCoupons', { code });
export const adminActiveShop = (shopId, userId, isActive) =>
  adminApi.post('/activeShop', { shopId, userId, isActive });
export const adminUnbindShop = (shopId, userId) => adminApi.post('/unbindShop', { shopId, userId });
export const adminBindShop = (shopId, userId) => adminApi.post('/bindShop', { shopId, userId });

export const getStoreList = () => userApi.get('/getStoreList', {});

export const userLogin = async ({ username, password }) => {
  const response = await userApi.post('/login', { username, password });
  if (!response.success) {
    throw new Error(response.message || 'User login failed');
  }

  localStorage.setItem('userToken', response.data?.token || '');
  localStorage.setItem('userUserId', response.data?.id || '');
  localStorage.setItem('userProfile', JSON.stringify(response.data || {}));
  return response.data;
};

export const userRegister = async (data) => {
  const response = await userApi.post('/register', data);
  if (!response.success) {
    throw new Error(response.message || 'User register failed');
  }
  return response.data;
};

export const userLogout = async () => {
  const response = await userApi.post('/logout');
  if (!response.success) {
    throw new Error(response.message || 'User logout failed');
  }

  localStorage.removeItem('userToken');
  localStorage.removeItem('userUserId');
  localStorage.removeItem('userProfile');
  return response.data;
};

export const applyCoupon = (code, userId = localStorage.getItem('userUserId')) =>
  userApi.post('/useCoupon', { code, userId });
export const bindShop = (shopId, userId = localStorage.getItem('userUserId')) =>
  userApi.post('/bindShop', { shopId, userId });
export const unbindShop = (shopId, userId = localStorage.getItem('userUserId')) =>
  userApi.post('/unbindShop', { shopId, userId });
export const getUserInfo = (userId = localStorage.getItem('userUserId')) =>
  userApi.post('/info', { userId });
export const changeUserPassword = ({ currentPassword, newPassword }) =>
  userApi.post('/change-password', { currentPassword, newPassword });
export const requestTemporaryPassword = (email) =>
  userApi.post('/request-temporary-password', { email });

export const adminUpdateUser = (userId, data) => adminApi.post('/updateUsers/', { ...data, userId });
export const adminDeleteUser = (userId) => adminApi.post('/deleteUsers', { userId });
export const adminResetUserPassword = (userId) => adminApi.post('/reset-user-password', { userId });
export const adminRequestTemporaryPassword = (email) =>
  adminApi.post('/request-temporary-password', { email });
export const adminChangePassword = ({ currentPassword, newPassword }) =>
  adminApi.post('/change-password', { currentPassword, newPassword });
export const requestPasswordReset = (email) => userApi.post('/password-reset/request', { email });
export const confirmPasswordReset = (token, password) =>
  userApi.post('/password-reset/confirm', { token, password });
